import { __awaiter } from "tslib";
import firebase from "firebase/app";
import "firebase/firestore";
import { getRootId } from "./root-id";
import { escapeKey } from "./fire-utils";
export function isFirestoreError(e) {
    return (e instanceof Error) && !!e.code;
}
// security rules violations are signaled as permissions errors
// for instance, requesting a non-existent document whose security rules depend on its contents
// results in a permissions error, because the non-existent document can't satisfy the rules
export function isFirestorePermissionsError(e) {
    return isFirestoreError(e) && (e.code === "permission-denied");
}
export class Firestore {
    constructor(db) {
        Object.defineProperty(this, "user", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "db", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.db = db;
    }
    setFirebaseUser(user) {
        this.user = user;
    }
    get userId() {
        return this.user ? this.user.uid : "no-user-id";
    }
    get isConnected() {
        return this.user !== null;
    }
    //
    // Firestore
    //
    getRootFolder() {
        const { appMode } = this.db.stores;
        return `/${appMode}/${getRootId(this.db.stores, this.userId)}/`;
    }
    getFullPath(path = "") {
        return `${this.getRootFolder()}${path}`;
    }
    getMulticlassSupportsPath() {
        return this.getFullPath("mcsupports");
    }
    getClassInfoPath(unit, classHash) {
        return this.getFullPath(`aicontent/${unit}/classes/${classHash}`);
    }
    getMulticlassSupportsRef() {
        return this.collectionRef(this.getMulticlassSupportsPath());
    }
    getMulticlassSupportDocumentPath(docId) {
        return `${this.getMulticlassSupportsPath()}/${docId}`;
    }
    getMulticlassSupportDocumentRef(docId) {
        return this.documentRef(this.getMulticlassSupportDocumentPath(docId));
    }
    collectionRef(fullPath) {
        return firebase.firestore().collection(fullPath);
    }
    documentRef(collectionOrFullDocumentPath, documentPath) {
        return documentPath
            ? firebase.firestore().collection(collectionOrFullDocumentPath).doc(escapeKey(documentPath))
            : firebase.firestore().doc(collectionOrFullDocumentPath);
    }
    collection(partialPath) {
        return firebase.firestore().collection(`${this.getRootFolder()}${partialPath}`);
    }
    doc(partialPath) {
        return firebase.firestore().doc(`${this.getRootFolder()}${partialPath}`);
    }
    newDocumentRef(collectionPath) {
        return firebase.firestore().collection(collectionPath).doc();
    }
    getDocument(collectionOrFullDocumentPath, documentPath) {
        const docRef = this.documentRef(collectionOrFullDocumentPath, documentPath);
        return docRef.get();
    }
    runTransaction(fn) {
        return firebase.firestore().runTransaction(fn);
    }
    /*
     * Guarantees the existence of the specified document by reading it first and then
     * creating it if it doesn't already exist. Optionally, client can specify a
     * `shouldUpdate()` function which determines whether the document should be written
     * even if it already exists (e.g. because the contents have changed). When either
     * creating or updating the document, the client's `writeContent()` function is called
     * to determine the content to be written. Returns the read content in the case of
     * preexisting documents that are not updated or the promise returned from the `set()`
     * function in the case of documents created or updated.
     */
    guaranteeDocument(partialPath, writeContent, shouldUpdate) {
        return __awaiter(this, void 0, void 0, function* () {
            const docRef = this.doc(partialPath);
            try {
                const content = (yield docRef.get()).data();
                if (shouldUpdate === null || shouldUpdate === void 0 ? void 0 : shouldUpdate(content)) {
                    // update the document if client indicates the need to do so
                    const _content = yield writeContent();
                    return _content ? docRef.set(_content, { merge: true }) : undefined;
                }
                return content;
            }
            catch (e) {
                // security rules violations (e.g. requests for non-existent documents whose security
                // rules depend on the contents of the document) are signaled as permissions errors
                if (isFirestorePermissionsError(e)) {
                    // create the document if it doesn't already exist
                    const _content = yield writeContent();
                    return _content ? docRef.set(_content) : undefined;
                }
            }
        });
    }
    batch(fn) {
        const batch = firebase.firestore().batch();
        fn(batch);
        return batch.commit();
    }
    timestamp() {
        return firebase.firestore.FieldValue.serverTimestamp();
    }
    getFirestoreUser(uid) {
        return __awaiter(this, void 0, void 0, function* () {
            const userDoc = yield this.doc(`users/${uid}`).get();
            return userDoc.data();
        });
    }
    /**
     * Record the lastLaunchTime in the Firestore root.
     *
     * This is only recorded for dev, qa, test, and demo appModes.
     * In the dev, qa, and test modes each user has their own root or a new root
     * is created on each test.
     * In the demo mode there could be lots of users launching in the same root
     * but this number should be manageable, and it will be useful to keep track
     * of how old various demo roots are.
     * In the auth (portal launch) case lots of users will be launching the same
     * root so the lastLaunchTime would be updated too frequently. Also we can use
     * logs and portal information to find the last portal launch.
     *
     * @returns a promise that resolves when the lastLaunchTime has been updated
     */
    recordLaunchTime() {
        return __awaiter(this, void 0, void 0, function* () {
            const { appMode } = this.db.stores;
            if (!["dev", "qa", "test", "demo"].includes(appMode)) {
                return;
            }
            return this.doc("").set({ lastLaunchTime: this.timestamp() }, { merge: true });
        });
    }
}
//# sourceMappingURL=data:application/json;base64,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